import { useEffect, useMemo, useRef, useState, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { HandoverPalette } from '@/views/handover/Palette';
import { KevoBar } from '@/components/KevoBar';
import { lazyFresh } from '@/lib/lazyFresh';
import type { Article, Country, Department, Lang } from '@/data/types';
import { COUNTRIES, DEPARTMENTS } from '@/data';
import { WORKERS, isGone, deptLabel } from '@/data/workers';
import { setCanReports } from '@/lib/canReports';
import { useUser } from '@/auth/AuthContext';
import { usePersistentState } from '@/hooks/usePersistentState';
import { useIsPhone, useIsDrawer } from '@/hooks/useIsPhone';
import { useProgress } from '@/hooks/useProgress';
import { TopBar } from '@/components/TopBar';
import { PlannerTimerChip } from '@/components/PlannerTimerChip';
import { markSeen } from '@/hooks/useSeen';
import { routeNotification, consumeNotifLink } from '@/lib/notifRoute';
import { setTicketFocus, setTaskFocus } from '@/hooks/useWorkFocus';
import { setAdminFocus } from '@/hooks/useDeepLink';
import type { PortalNotif } from '@/hooks/useNotifications';
import { setMyWorkTab, getMyWorkTab } from '@/hooks/useMyWorkTab';
import { useRoute } from '@/hooks/useRoute';
import { pathOf, standaloneFromReturn, forgetStandaloneReturn } from '@/lib/route';
import { go, dropParams } from '@/lib/navigator';
import type { CountryCode } from '@/integrations/types';
import { Sidebar, type Section, type DocsTab } from '@/components/FloatingDock';
import { ActesView } from '@/views/actes/ActesView';
import { actesCountryOf } from '@/lib/actesCountry';
import { InstallApp } from '@/components/InstallApp';
import { isSuperAdmin } from '@/config/admins';
import { allowedCountries, isMyWorkSuspended, workerFor, canUsePlanner } from '@/config/access';
import { myAccount, type MyAccount } from '@/integrations/admin';
import { usePendingAccounts } from '@/hooks/useWorkspaceData';
import { Onboarding } from '@/components/Onboarding';
import { Profile } from '@/components/Profile';
import { Login } from '@/components/Login';
import { ArticleDrawer } from '@/components/ArticleDrawer';
import { Toast, TOAST_EVENT, type ToastState, type ToastAction } from '@/components/Toast';
import { useQuery } from '@tanstack/react-query';
import { fetchPortalConfig } from '@/integrations/portalConfig';
import { setStageOverrides } from '@/lib/stageGroups';
import { setRosterOverrides } from '@/lib/rosterOverrides';
import { setCopyOverrides } from '@/lib/portalCopy';
import { hydrateSeenFromServer } from '@/hooks/useSeen';
import { hydrateAvatarFromServer } from '@/hooks/useAvatar';
import { hydratePrefsFromServer } from '@/hooks/useSyncedPref';
import { useLiveSync } from '@/hooks/useLiveSync';
import { refreshPushIfGranted } from '@/lib/webPush';
import { gchatApi, setGChatReadsOwner } from '@/integrations/googleChatApi';
import { KBIcon } from '@/components/kbIcons';

// Code-split the heavy views so the first load only ships the shell; each view
// (and its dependencies) is fetched on demand the first time it's opened.
// `lazyFresh`: if a deploy swapped the chunks under this open page, the failed
// import reloads once instead of showing a new user the crash screen.
const QuickAccessView = lazyFresh(() => import('@/views/QuickAccessView').then((m) => ({ default: m.QuickAccessView })));
const ResourcesHubView = lazyFresh(() => import('@/views/ResourcesHubView').then((m) => ({ default: m.ResourcesHubView })));
const DocumentsView = lazyFresh(() => import('@/views/DocumentsView').then((m) => ({ default: m.DocumentsView })));
const MyWorkView = lazyFresh(() => import('@/views/MyWorkView').then((m) => ({ default: m.MyWorkView })));
const AdminView = lazyFresh(() => import('@/views/AdminView').then((m) => ({ default: m.AdminView })));
const StatsView = lazyFresh(() => import('@/views/StatsView').then((m) => ({ default: m.StatsView })));
const PlannerView = lazyFresh(() => import('@/views/PlannerView').then((m) => ({ default: m.PlannerView })));
const CalendarView = lazyFresh(() => import('@/views/CalendarView').then((m) => ({ default: m.CalendarView })));
const InboxView = lazyFresh(() => import('@/views/InboxView').then((m) => ({ default: m.InboxView })));
const ChatView = lazyFresh(() => import('@/views/chat/ChatView').then((m) => ({ default: m.ChatView })));
const ChatDock = lazyFresh(() => import('@/views/chat/ChatDock').then((m) => ({ default: m.ChatDock })));
const GoogleChatAlerts = lazyFresh(() => import('@/views/chat/GoogleChatAlerts').then((m) => ({ default: m.GoogleChatAlerts })));
import { useNewVersion } from '@/hooks/useNewVersion';
import { CustomerChatSoon } from '@/views/chat/CustomerChatSoon';
const NotificationsView = lazyFresh(() => import('@/views/NotificationsView').then((m) => ({ default: m.NotificationsView })));
/* The Visitor Visa journey: a link-only landing, outside the portal's frame. */
const VisitorVisaPage = lazyFresh(() => import('@/views/visitorVisa/VisitorVisaPage').then((m) => ({ default: m.VisitorVisaPage })));
/* Kevo's agent, on the right of every page — its own chunk, fetched once the shell is up. */
const KevoAgent = lazyFresh(() => import('@/views/agent/KevoAgent').then((m) => ({ default: m.KevoAgent })));
const FeedbackModal = lazyFresh(() => import('@/components/FeedbackModal').then((m) => ({ default: m.FeedbackModal })));
// AI Assistant and Logbook are disabled — removed with their dead code.

const VISITOR_VISA_LOADING = <div className="onb"><div style={{ color: 'var(--ink-3)', fontFamily: 'var(--font-display)', fontSize: 22 }}>Loading…</div></div>;

/* /visitor-visa is a presentation page open WITHOUT signing in (Gerónimo,
   23 Sept 2026). Its route sits beside the portal's, not under it, so a
   visitor never meets the login screen — and its «Back to the Agent Portal»
   link (/home) asks for one. */
export function VisitorVisaScreen() {
  forgetStandaloneReturn();
  return (
    <Suspense fallback={VISITOR_VISA_LOADING}>
      <VisitorVisaPage />
    </Suspense>
  );
}

export default function App() {
  const { user, loading, authConfigured } = useUser();

  // -- auth gate (before the app shell; onboarding gate stays after) --
  if (loading) {
    return (
      <div className="onb">
        <div style={{ color: 'var(--ink-3)', fontFamily: 'var(--font-display)', fontSize: 22 }}>Loading…</div>
      </div>
    );
  }
  if (authConfigured && !user) {
    return <Login />;
  }

  /* Someone who signed in with Google from the page (when it still asked for
     a login) is brought back to it, not to /home: no sidebar, no onboarding. */
  if (standaloneFromReturn() === 'visitor-visa') return <VisitorVisaScreen />;

  // Past the gate `user` is guaranteed non-null (demo user when unconfigured).
  return <AppShell user={user!} />;
}

/* Read once per page load, before the router rewrites the address. */
const linkCountry = new URLSearchParams(window.location.search).get('country');

const goSection = (sec: Section, docsTab?: DocsTab) => go(pathOf({ section: sec, mwTab: getMyWorkTab(), docsTab }));

function AppShell({ user }: { user: NonNullable<ReturnType<typeof useUser>['user']> }) {
  // -- persisted navigation/preferences --
  const [country, setCountry] = usePersistentState<Country | null>('lexidy-kb-country', null);
  const [dept, setDept] = usePersistentState<Department | null>('lexidy-kb-dept', null);
  const [lang, setLang] = usePersistentState<Lang>('lexidy-kb-lang', 'en');
  /* THE ADDRESS BAR IS THE ONLY COPY of where you are. Every page has a path
     of its own (/tickets, /reports, /tickets/1234), so a typed URL, a
     bookmark, a shared link and the browser's Back button are all just a
     route; the entry points (`/`, `?go=notifications`) are redirected by the
     router before anything renders. What this tab last had open is kept only
     to answer `/` after a reload. */
  const route = useRoute();
  const section: Section = route?.section ?? 'tools';
  useEffect(() => {
    try { sessionStorage.setItem('lexidy-kb-section', section); } catch { /* storage off — fine */ }
  }, [section]);
  /* A link can carry its own jurisdiction — Kevo linking straight to one
     task doesn't know which country this browser last had open, only which
     one the task lives in. Consumed once and wiped from the address bar;
     the "enforce jurisdiction" effect further down still has the last word
     if this viewer isn't allowed to see that country. */
  useEffect(() => {
    if (!linkCountry) return;
    const c = COUNTRIES.find((co) => co.code === linkCountry.toUpperCase());
    if (c) setCountry(c);
    dropParams('country');
  }, []);
  /* A LINK FROM AN E-MAIL, OR FROM A PUSH. Both carry `?n=<kind>&ref=<id>`
     (and `focus`/`tab` for Actes), which is handed to the SAME routing a
     click on the bell uses — so "Marie Dupont changed stage" opens Marie
     Dupont's case, on its activity module, instead of the home screen. */
  useEffect(() => { consumeNotifLink((sec) => goSection(sec as Section)); }, []);
  // Where the user was before opening Notifications — powers its Back button.
  const [notifReturn, setNotifReturn] = useState<Section>('tools');
  const docsTab: DocsTab = route?.docsTab ?? 'services';
  const docsHub = !route?.docsTab;
  const [sidebarCollapsed, setSidebarCollapsed] = usePersistentState<boolean>('lexidy-kb-sb-collapsed', false);
  /* What the rail was doing before a section asked it to step aside. */
  const railBefore = useRef<boolean | null>(null);
  // Phones and portrait tablets start with the sidebar as a rail (opening it
  // overlays as a drawer). 900 is the app's structural breakpoint — the CSS
  // that turns the sidebar into an overlay uses it, and so does useIsPhone.
  // This used to say 760: between 760 and 900 the drawer covered the content
  // and nothing ever closed it again.
  const isNarrow = useIsPhone();
  /* …and, separately, whether it is a DRAWER right now — see useIsDrawer. */
  const isDrawer = useIsDrawer();
  useEffect(() => {
    if (isNarrow) setSidebarCollapsed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNarrow]);
  const [theme, setTheme] = usePersistentState<'light' | 'dark'>('lexidy-kb-theme', 'light');
  /* Has the Inbox been opened this visit? See the note where it renders.
     ABOVE THE GATES ON PURPOSE. The first version sat under the
     <AccessPending /> / <BootWait /> early returns, so anyone hitting a gate
     rendered fewer hooks on one pass and more on the next — React #310, the
     whole France team locked out at the door. Every hook in this function
     stays above the first `return`. `section` is read here, not
     `activeSection`, because that guard is computed below the gates; the two
     agree for 'inbox', which the guard never remaps. */
  const [inboxSeen, setInboxSeen] = useState(false);
  useEffect(() => { if (section === 'inbox') setInboxSeen(true); }, [section]);
  /* The Inbox holds Kevo's mail and Google Chat — the person's own, connected
     by signing in with Google — once the server has Google switched on. */
  const gchatStatus = useQuery({ queryKey: ['gchat-status'], queryFn: () => gchatApi.status(), staleTime: 60_000, retry: 1 });
  const chatOn = gchatStatus.data?.configured === true;
  const [savedInboxMode, setInboxMode] = usePersistentState<'mail' | 'chat' | 'customer'>('lexidy-inbox-mode', 'mail');
  const inboxMode = chatOn ? savedInboxMode : 'mail';
  /* EVERYTHING IS GOOGLE CHAT (Gerónimo, 8 Oct 2026). The chat's badge counts
     what the chat lists: Google's conversations with something unread (not
     the silenced ones). Conversations, not messages: Google only says whether
     one is unread. Same keys as the chat and the pop-ups, so it is one
     request for all three. Access revoked: no count. */
  const gchatOn = gchatStatus.data?.connected === true && !/expired or revoked|invalid_grant/i.test(gchatStatus.data.lastError || '');
  const gchatWho = user.email.toLowerCase();
  /* The conversations read in this browser are this person's (the bubbles,
     the counts and the chat all read them). */
  setGChatReadsOwner(gchatWho);
  const gchatSpaces = useQuery({
    queryKey: ['gchat-spaces'],
    queryFn: () => import('@/views/chat/GoogleChatPane').then((m) => m.fetchGChatSpaces(gchatWho)),
    enabled: gchatOn, staleTime: 20_000, retry: 1,
  });
  const chatUnread = gchatOn ? (gchatSpaces.data || []).filter((s) => s.unread === true && s.muted !== true).length : 0;
  /* THE TAB SAYS IT TOO (8 Oct 2026): «(3) Lexidy · Agent Portal» while
     something is unread, so a portal left in a background tab still shows
     there is something waiting, as Google Chat's tab does. Only a prefix: the
     title stays whatever it is, the count goes in front, and comes off at 0. */
  useEffect(() => {
    const strip = (t: string) => t.replace(/^\(\d+\+?\) /, '');
    const apply = () => {
      const base = strip(document.title);
      const want = chatUnread > 0 ? `(${chatUnread > 99 ? '99+' : chatUnread}) ${base}` : base;
      if (document.title !== want) document.title = want;
    };
    apply();
    /* A screen that sets its own title later keeps it — with the count put back in front. */
    const el = document.querySelector('title');
    const mo = el && typeof MutationObserver !== 'undefined' ? new MutationObserver(apply) : null;
    if (mo && el) mo.observe(el, { childList: true, characterData: true, subtree: true });
    return () => { mo?.disconnect(); document.title = strip(document.title); };
  }, [chatUnread]);
  const [jacSeen, setJacSeen] = useState(false);
  useEffect(() => { if (section === 'inbox' && inboxMode === 'mail') setJacSeen(true); }, [section, inboxMode]);
  /* KEVO GETS THE ROOM (Gerónimo, 1 Oct 2026): entering the Inbox folds
     the sidebar; leaving it puts the sidebar back the way it was. Opening it
     by hand while in the Inbox is respected until you leave. */
  const sbBeforeInbox = useRef<boolean | null>(null);
  useEffect(() => {
    if (section === 'inbox') {
      if (sbBeforeInbox.current === null) { sbBeforeInbox.current = sidebarCollapsed; setSidebarCollapsed(true); }
    } else if (sbBeforeInbox.current !== null) {
      setSidebarCollapsed(sbBeforeInbox.current);
      sbBeforeInbox.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);
  /* "Open this case" from wherever a case is named — a note's window, for
     one. One event, one handler, no prop threaded through six components.
     (Above the gates, like every hook in this component.) */
  useEffect(() => {
    /* A case or a task named with its country (a card shared in the chat
       may come from another country): the top bar moves there first when
       this person works it. */
    const toCountry = (code?: string) => {
      const cc = String(code || '').toUpperCase();
      if (!cc || countryRef.current?.code === cc) return;
      const c = allowedRef.current.find((x) => x.code === cc);
      if (c) setCountry(c);
    };
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent<{ id?: string; country?: string }>).detail || {};
      const id = String(d.id || '');
      if (!id) return;
      toCountry(d.country);
      setMyWorkTab('tickets', { pulse: false }); setTicketFocus(id); navigate('mywork');
    };
    const onTask = (e: Event) => {
      const d = (e as CustomEvent<{ id?: string; country?: string }>).detail || {};
      if (!d.id) return;
      toCountry(d.country);
      setMyWorkTab('tasks', { pulse: false }); setTaskFocus(String(d.id)); navigate('mywork');
    };
    /* And the way back: a dossier on a case page asks for the Actes section,
       which reads which dossier from the one-shot focus store. Same door,
       opposite direction — neither side is a dead end. */
    /* LE BOUTON « OUVRIR » NE MENAIT NULLE PART DEPUIS UN AUTRE PAYS.
       La section Actes est gardée par `country.code !== 'FR'` : depuis un
       dossier ouvert alors que la barre du haut était sur l'Espagne, la
       navigation partait bien et le garde renvoyait aussitôt sur Quick
       Access — sans rien dire. On passe la barre sur la France d'abord,
       quand cette personne a le droit d'y aller ; sinon on le dit. */
    const onActes = () => {
      const cur = countryRef.current;
      /* Portugal has its own Actes now: from a Portuguese case, stay in Portugal. */
      if (!actesCountryOf(cur?.code)) {
        const fr = allowedRef.current.find((c) => c.code === 'FR');
        if (!fr) {
          showToast(lang === 'es'
            ? 'France · Actes es una sección de Francia y no tienes acceso a Francia.'
            : 'France · Actes is a French section and you do not have access to France.');
          return;
        }
        setCountry(fr);
      }
      navigate('actes');
    };
    const onToast = (e: Event) => {
      const d = (e as CustomEvent<ToastState>).detail;
      if (d?.msg) showToast(d.msg, d.kind, d.action);
    };
    window.addEventListener('lexidy:open-case', onOpen);
    window.addEventListener('lexidy:open-task', onTask);
    window.addEventListener('lexidy:open-actes', onActes);
    window.addEventListener(TOAST_EVENT, onToast);
    return () => {
      window.removeEventListener('lexidy:open-case', onOpen);
      window.removeEventListener('lexidy:open-task', onTask);
      window.removeEventListener('lexidy:open-actes', onActes);
      window.removeEventListener(TOAST_EVENT, onToast);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.documentElement.dataset.theme = theme; }, [theme]);

  // Super-admin status comes from the server account (DB role OR seed list);
  // fall back to the code seed list before the account loads. Super admins get
  // the Admin panel, all jurisdictions, and role management.
  // A newer deploy is live — background tabs reload alone; a front tab gets the banner.
  const newVersion = useNewVersion();
  const seedSuper = isSuperAdmin(user.email);

  // Account approval status (viewers not on the roster need admin approval +
  // a country assignment). Fail-open: roster members and super admins are
  // auto-approved server-side and never blocked here.
  const [account, setAccount] = useState<MyAccount | null>(null);
  /* The account has answered, or failed. Until then nobody knows whether this
     is a super admin: the seed list above has two names and the database more. */
  const [accountSettled, setAccountSettled] = useState(false);
  useEffect(() => {
    let ok = true;
    setAccountSettled(false);
    myAccount()
      .then((a) => { if (ok) setAccount(a); })
      .catch(() => { /* fail-open */ })
      .finally(() => { if (ok) setAccountSettled(true); });
    return () => { ok = false; };
  }, [user.email]);

  const isSuper = account?.isSuperAdmin ?? seedSuper;

  // Live count of pending access requests (super admins only) — drives the
  // sidebar bell and the Quick Access notifications. Polls, so it updates
  // without anyone reloading.
  const pendingAccounts = usePendingAccounts(isSuper);
  const notifCount = pendingAccounts.data?.length ?? 0;

  // Self-service portal config: announcement banners (rendered by Quick Access)
  // and admin-pinned stage→column mappings, injected into the kanban grouping.
  const portalCfg = useQuery({
    queryKey: ['portal-config', country?.code || ''] as const,
    queryFn: ({ signal }) => fetchPortalConfig(country?.code || '', signal).catch(() => ({ notices: [], stageMap: [], roster: [], copy: [] })),
    enabled: !!country,
    refetchInterval: 5 * 60_000,
    staleTime: 4 * 60_000,
  });
  const stageMap = portalCfg.data?.stageMap;
  useEffect(() => { if (stageMap) setStageOverrides(stageMap); }, [stageMap]);
  const rosterOv = portalCfg.data?.roster;
  useEffect(() => { if (rosterOv) setRosterOverrides(rosterOv); }, [rosterOv]);
  const copyOv = portalCfg.data?.copy;
  useEffect(() => { if (copyOv) setCopyOverrides(copyOv); }, [copyOv]);
  // Read-notification state + profile photo sync per account (any device).
  useEffect(() => {
    void hydrateSeenFromServer();
    void hydrateAvatarFromServer(user.email);
    void hydratePrefsFromServer();
    void refreshPushIfGranted();
  }, [user.email]);
  // Real-time: every open tab listens on one Supabase Realtime channel and
  // refreshes the affected slice the moment anyone changes something.
  useLiveSync(user.email);

  // Jurisdictions this user may see/switch to (super admins: all; roster
  // members: their own country; approved viewers: their assigned country).
  // If the country/dept redirect effects never resolve, don't wait forever:
  // after a few seconds we show the picker rather than a blank page.
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGaveUp(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const allowed = useMemo(
    () => allowedCountries(user.email, account?.assignedCountry, isSuper),
    [user.email, account?.assignedCountry, isSuper],
  );
  const onRoster = !!workerFor(user.email);

  const { signOut } = useUser();
  const { read, toggleRead } = useProgress(user.email);

  // -- ui state --
  const [openArticle, setOpenArticle] = useState<Article | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Redirect each logged-in roster user to THEIR jurisdiction, once per login
  // (so it overrides whatever country was last persisted on this browser).
  // People not in the roster (e.g. managers) keep the picker / their choice.
  const defaultDept = DEPARTMENTS.find((d) => d.code === 'immigration') || DEPARTMENTS[0];
  const me = useMemo(
    () => WORKERS.find((w) => w.email?.toLowerCase() === user.email.toLowerCase()) ?? null,
    [user.email],
  );
  useEffect(() => {
    let last: string | null = null;
    try {
      last = localStorage.getItem('lexidy-kb-last-user');
    } catch {
      /* ignore */
    }
    if (last === user.email) return; // already redirected this user here
    try {
      localStorage.setItem('lexidy-kb-last-user', user.email);
    } catch {
      /* ignore */
    }
    if (me) {
      const c = COUNTRIES.find((co) => co.code === me.country);
      if (c) {
        setCountry(c);
        setDept(defaultDept);
      }
    }
  }, [user.email]);

  // Safety net: a roster member must always have a jurisdiction. If country/dept
  // ever ends up null (e.g. legacy state), restore it from the roster so the app
  // never gets stuck on a blank onboarding gate.
  useEffect(() => {
    if (me && (!country || !dept)) {
      const c = COUNTRIES.find((co) => co.code === me.country);
      if (c) {
        if (!country) setCountry(c);
        if (!dept) setDept(defaultDept);
      }
    }
  }, [me, country, dept]);

  // Enforce jurisdiction access: if the persisted country is not one this user
  // is allowed to see (e.g. a France paralegal with stale Spain state), snap
  // them to their allowed jurisdiction. Super admins are allowed everywhere.
  useEffect(() => {
    if (!country) return;
    /* Not before the account says who this is: a super admin missing from the
       seed list would be sent back to their own country, and it would stick. */
    if (!accountSettled) return;
    if (!allowed.length) return; // nothing to snap to — leave them be
    if (!allowed.some((c) => c.code === country.code)) {
      setCountry(allowed[0]);
    }
  }, [country, allowed, accountSettled]);

  // Approved viewers (not on the roster) get their admin-assigned country.
  useEffect(() => {
    if (me) return;
    if (account?.status !== 'approved' || !account.assignedCountry) return;
    // The gate below needs BOTH country and dept. Checking only `country` left
    // an approved viewer with a stored country but no dept staring at a blank
    // page forever, because the gate returned null waiting for an effect that
    // could never fire.
    if (country && dept) return;
    const c = COUNTRIES.find((co) => co.code === account.assignedCountry!.toUpperCase());
    if (!country && c) setCountry(c);
    if (!dept) setDept(defaultDept);
  }, [account, me, country, dept]);

  /* ── DOS `useRef` DEBAJO DE DOS PUERTAS: EL FALLO #310 DE VICTOIRE ──
     Estaban escritos más abajo, detrás de `AccessPending` y de la puerta
     `if (!country || !dept)`. En los renders donde una puerta salta, React
     cuenta DOS hooks menos que en los demás — y eso es literalmente lo que
     React #310 dice: «un componente ha renderizado un conjunto de hooks
     distinto al de su render anterior». La pantalla se caía al arrancar,
     entre el momento en que la cuenta aún no ha llegado y el momento en que
     ya está: tres veces seguidas en el registro de Victoire, a las 12:01,
     12:03 y 12:05.
     No dependen de nada de lo que hay debajo: suben, y la cuenta de hooks
     deja de moverse.

     El escuchador de eventos se registra una sola vez, así que necesita
     referencias vivas y no los valores del primer render. */
  const countryRef = useRef(country);
  countryRef.current = country;
  const allowedRef = useRef(allowed);
  allowedRef.current = allowed;

  /* Con acción («Deshacer») el toast vive seis segundos, no dos y medio: hay
     que darle tiempo a la mano. Y un toast nuevo cancela el reloj del anterior,
     que antes lo cerraba a destiempo. */
  const toastTimer = useRef<number | undefined>(undefined);
  const showToast = (msg: string, kind: ToastState['kind'] = '', action?: ToastAction) => {
    window.clearTimeout(toastTimer.current);
    setToast({ msg, kind, action });
    toastTimer.current = window.setTimeout(() => setToast(null), action ? 6000 : 2400);
  };

  // -- denied gate: a super admin can still deny a viewer who is not on the
  //    roster. New accounts arrive approved; a stray 'pending' is let through. --
  if (account && !isSuper && !onRoster && account.status === 'denied') {
    return <AccessPending lang={lang} status="denied" onSignOut={() => void signOut()} />;
  }
  /* Somebody who has left the firm: access closed (24 Sept 2026). Their work
     stays visible to everyone; only their own entry is shut — the server
     answers 403 to their session as well. */
  if (me && isGone(me)) {
    return <AccessPending lang={lang} status="closed" onSignOut={() => void signOut()} />;
  }

  // -- onboarding gate (roster users are auto-redirected above) --
  if (!country || !dept) {
    // Approved viewers get their admin-assigned country (skip the picker), and
    // roster members are redirected by the effects above. Both are one-frame
    // waits — but a wait that renders nothing is indistinguishable from a
    // broken app, so it shows a spinner and gives up after a moment into the
    // picker instead of leaving a white screen behind.
    const waiting =
      (!me && account?.status === 'approved' && !!account.assignedCountry
        && COUNTRIES.some((co) => co.code === account.assignedCountry!.toUpperCase()))
      || !!me;
    if (waiting && !gaveUp) return <BootWait />;
    return (
      <Onboarding
        onComplete={({ country: c, dept: d }) => {
          setCountry(c);
          setDept(d);
        }}
      />
    );
  }

  // -- handlers --
  const handleToggleRead = (id: string) => {
    const added = toggleRead(id);
    if (added) showToast(lang === 'es' ? 'Marcado como leído ✓' : 'Marked as read ✓', 'success');
  };

  /**
   * Where a notification takes you — the whole journey, not the doorstep.
   *
   * Three rules learned the hard way:
   *  · An admin alert lands on its PANEL (the feedback itself), not on the
   *    Administration home for you to go hunting.
   *  · A client's case NEVER throws you out of the portal. Documents landed in
   *    Parallel? You go to the CASE, on its documents module — Parallel and
   *    HubSpot are one button away from there, if you want them.
   *  · The case page opens on the module the news is about: the task list for
   *    a task, notes for a note, documents for documents.
   */
  const goNotification = (n: PortalNotif) => {
    routeNotification(
      { kind: n.kind, refId: n.refId, target: n.target, tab: n.tab },
      (sec) => goSection(sec as Section),
    );
  };

  /* The chat, from the top bar or a bubble's «open in the chat». */
  const openChat = () => {
    if (inboxMode !== 'chat') setInboxMode('chat');
    goSection('inbox');
  };

  const navigate = (sec: Section, tab?: DocsTab) => {
    // Entering Notifications remembers where you came from (for its Back button).
    if (sec === 'notifications' && section !== 'notifications') setNotifReturn(section);
    goSection(sec, tab); // no explicit tab → Resources lands on the hub cards
    /* Close it after navigating ONLY while it is a drawer covering the page
       (≤900px, the threshold the CSS overlay itself uses). Above that the
       sidebar sits beside the content and hides nothing, so folding it would
       be undoing a choice the person made — and the only section that folds
       the rail on purpose is Actes, which carries its own navigation and puts
       it back the way it found it. */
    if (isDrawer) setSidebarCollapsed(true);
  };

  const changeCountry = (c: Country) => {
    if (!allowed.some((a) => a.code === c.code)) return; // not permitted for this user
    setCountry(c);
    showToast(lang === 'es' ? `Cambiado a ${c.name}` : `Switched to ${c.name}`, 'success');
  };
  const changeDept = (d: Department) => {
    setDept(d);
    showToast(lang === 'es' ? `Departamento: ${d.name}` : `Department: ${d.name}`, 'success');
  };

  /* The three tiers: ordinary users see neither section; heads see
     Statistics plus the admin panel reduced to Packages; supers see all. */
  const isHeadRole = Boolean(account?.isHead);
  const canStats = isSuper || isHeadRole;
  setCanReports(canStats);
  const canAdminPanel = isSuper || isHeadRole;
  const canPlanner = canUsePlanner(user.email, isSuper);

  // Guard: if someone lands on a section they have no access to (a stale
  // link), show Quick Access — and, once the account has said who this is,
  // move the address there too. Not before: a super admin missing from the
  // seed list would lose /admin to the wait.
  const activeSection: Section =
    (section === 'admin' && !canAdminPanel) || (section === 'stats' && !canStats) || (section === 'planner' && !canPlanner)
    || (section === 'actes' && !actesCountryOf(country.code))
      ? 'tools' : section;
  const lostAccess = accountSettled && activeSection !== section;

  const screenLabel =
    activeSection === 'tools'
      ? 'Quick Access & Tools'
      : activeSection === 'mywork'
        ? `My work · ${user.name}`
        : activeSection === 'admin'
          ? 'Administration'
          : activeSection === 'stats'
            ? 'Statistics'
            : activeSection === 'planner'
              ? 'Planner'
              : activeSection === 'notifications'
              ? 'Notifications'
              : `Documents · ${docsTab}`;

  const inboxSwitch = chatOn ? (
    <div className={`inbox-switch three ${inboxMode === 'customer' ? 'at-3' : inboxMode === 'chat' ? 'at-2' : 'at-1'}`} role="tablist" aria-label="Inbox">
      <button type="button" role="tab" aria-selected={inboxMode === 'mail'} className={inboxMode === 'mail' ? 'on' : ''} onClick={() => setInboxMode('mail')}>
        <KBIcon name="mail" size={14} />
        <span>{lang === 'es' ? 'Correo · Kevo' : lang === 'fr' ? 'Courrier · Kevo' : lang === 'pt' ? 'Correio · Kevo' : 'Mail · Kevo'}</span>
      </button>
      <button type="button" role="tab" aria-selected={inboxMode === 'chat'} className={inboxMode === 'chat' ? 'on' : ''} onClick={() => setInboxMode('chat')}>
        <KBIcon name="users" size={14} />
        <span>{lang === 'es' ? 'Chat interno' : lang === 'fr' ? 'Chat interne' : lang === 'pt' ? 'Chat interno' : 'Team chat'}</span>
        {chatUnread > 0 && <b>{chatUnread > 99 ? '99+' : chatUnread}</b>}
      </button>
      {/* The third room: clients from the Customer Portal (Gerónimo, 29 Oct 2026) — «very soon». */}
      <button type="button" role="tab" aria-selected={inboxMode === 'customer'} className={inboxMode === 'customer' ? 'on' : ''} onClick={() => setInboxMode('customer')}>
        <KBIcon name="chat" size={14} />
        <span>{lang === 'es' ? 'Chat clientes' : lang === 'fr' ? 'Chat clients' : lang === 'pt' ? 'Chat clientes' : 'Customer chat'}</span>
        <i className="inbox-soon">{lang === 'es' ? 'pronto' : lang === 'fr' ? 'bientôt' : lang === 'pt' ? 'breve' : 'soon'}</i>
      </button>
    </div>
  ) : null;

  return (
    <div className={`app app-sb ${sidebarCollapsed ? 'sb-collapsed' : ''}`} data-screen-label={screenLabel}>
      {lostAccess && <Navigate to="/home" replace />}
      {newVersion && (
        <div className="nv-banner" role="status">
          <span>✨ {lang === 'es' ? 'Hay una versión nueva del portal' : lang === 'fr' ? 'Une nouvelle version du portail est là' : 'A new version of the portal is live'}</span>
          <button onClick={() => window.location.reload()}>
            {lang === 'es' ? 'Actualizar' : lang === 'fr' ? 'Actualiser' : 'Update'}
          </button>
        </div>
      )}
      {/* For anybody still using the portal in a browser tab: a card at the
          right edge offering the real install. It hides itself for good once
          the app is installed. */}
      <InstallApp lang={lang} />
      {/* Collapsible left sidebar — primary sections (replaces the floating dock). */}
      <Sidebar
        section={activeSection}
        onNavigate={navigate}
        lang={lang}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((v) => !v)}
        showAdmin={canAdminPanel}
        showStats={canStats}
        showPlanner={canPlanner}
        /* Kevo for everybody (Gerónimo, 1 Oct 2026). */
        showInbox
        notifCount={notifCount}
        countryCode={country.code}
        onFeedback={() => setFeedbackOpen(true)}
        userEmail={user.email}
        userName={user.name}
        userCountry={country.name}
        onOpenProfile={() => setProfileOpen(true)}
      />
      {/* On a narrow screen the open sidebar floats OVER the page, and the only
          way out was to tap a nav item. A dimmed backdrop both says "this is a
          layer" and gives the gesture everyone already tries: tap beside it. */}
      {isDrawer && !sidebarCollapsed && (
        <button
          className="sidebar-scrim"
          aria-label={lang === 'es' ? 'Cerrar menú' : lang === 'fr' ? 'Fermer le menu' : 'Close menu'}
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Floating frosted top bar: brand + jurisdiction/department, language, sign-out. */}
      <TopBar
        country={country}
        dept={dept}
        lang={lang}
        setLang={setLang}
        onChangeCountry={changeCountry}
        onChangeDept={changeDept}
        countries={allowed}
        theme={theme}
        onToggleTheme={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        isSuper={isSuper}
        onOpenNotifications={() => { setNotifReturn(activeSection); goSection('notifications'); }}
        onGoNotification={(n) => { markSeen([n.id]); goNotification(n); }}
        personDept={deptLabel(WORKERS.find((w) => w.email?.toLowerCase() === user.email.toLowerCase()))}
        chatUnread={chatUnread}
        onOpenChat={chatOn ? openChat : undefined}
      />
      {/* The chat's bubbles, bottom right — everywhere but the chat itself. */}
      {gchatOn && (
        <Suspense fallback={null}>
          <ChatDock lang={lang} me={user.email} hidden={activeSection === 'inbox' && inboxMode === 'chat'} onExpand={openChat} />
        </Suspense>
      )}
      {/* Google Chat pop-ups (6 Oct 2026; cards bottom right since 15 Oct): a
          direct message always, a group or space only if it mentions you.
          Mounted for everyone once Google is switched on; it stays silent
          until their Google account is connected. */}
      {chatOn && <Suspense fallback={null}><GoogleChatAlerts lang={lang} /></Suspense>}

      {/* My work uses the full page width (wide Kanban); other views stay readable. */}
      {/* France · Actes joins the wide pages. Its tables carry a person, a
          type, an authority, a reference, four dates and an owner: at 1140px
          every one of those columns truncated while the page kept two fat
          margins doing nothing. */}
      <main className={`main ${activeSection === 'mywork' || activeSection === 'inbox' || activeSection === 'actes' ? 'main--wide' : ''} ${activeSection === 'inbox' ? 'main--inbox' : ''}`}>
        <Suspense fallback={<div className="view-loading">{lang === 'es' ? 'Cargando…' : lang === 'fr' ? 'Chargement…' : 'Loading…'}</div>}>
        {activeSection === 'tools' && (
          <QuickAccessView
            country={country}
            dept={dept}
            lang={lang}
            onToast={showToast}
            onOpenProfile={() => setProfileOpen(true)}
            onOpenItem={(t) => {
              /* "Your day" → detail → the exact thing: the task opens on the
                 tasks board, the case on its own page. */
              if (t.kind === 'task') { setTaskFocus(t.id); setMyWorkTab('tasks'); }
              else { setMyWorkTab('tickets', { pulse: false }); setTicketFocus(t.id); }
              navigate('mywork');
            }}
          />
        )}
        {activeSection === 'mywork' && (
          isMyWorkSuspended(country.code)
            ? <ComingSoon lang={lang} country={country.name} />
            : <MyWorkView country={country} lang={lang} isSuper={isSuper} onToast={showToast} />
        )}
        {/* France's civil records. It carries its own top navigation, so it
            asks the portal's rail to fold while it is open — one rail at a
            time, and always clear which one you are steering. */}
        {activeSection === 'actes' && actesCountryOf(country.code) && (
          <ActesView
            /* France's, or Portugal's (lighter, in English): the top bar decides. */
            key={country.code}
            country={actesCountryOf(country.code)!}
            lang={lang}
            onChrome={(quiet) => {
              /* Actes carries a navigation of its own, so the portal's rail
                 folds while you are in it — and comes BACK the way you left
                 it. Remembering the previous state matters: somebody who
                 works with the rail collapsed must not find it open again
                 every time they leave a section. */
              if (quiet) { railBefore.current = sidebarCollapsed; setSidebarCollapsed(true); }
              else if (railBefore.current === false) setSidebarCollapsed(false);
            }}
          />
        )}
        {activeSection === 'admin' && canAdminPanel && <AdminView lang={lang} email={user.email} isSuper={isSuper} isHead={isHeadRole} />}
        {activeSection === 'stats' && canStats && <StatsView lang={lang} isSuper={isSuper} email={user.email} />}
        {activeSection === 'planner' && canPlanner && <PlannerView />}
        {activeSection === 'calendar' && (
          <CalendarView
            country={country.code as CountryCode}
            lang={lang}
            onOpenCase={(id) => { setMyWorkTab('tickets', { pulse: false }); setTicketFocus(id); navigate('mywork'); }}
            onOpenTask={(id) => { setTaskFocus(id); setMyWorkTab('tasks'); navigate('mywork'); }}
          />
        )}
        {/* Kevo, framed. It carries no country: the mail is the person's,
            not the country's. */}
        {/* KEVO STAYS ALIVE. Unmounting the section tore the frame down,
            so every return to Inbox reloaded Kevo from nothing. Once you
            have opened it, it stays mounted and is merely hidden while you are
            elsewhere — a hidden frame keeps its state, its polling and its
            scroll position. Never mounted until first opened, so people who
            never open it pay nothing. */}
        {inboxSeen && (
          <div hidden={activeSection !== 'inbox'} className="jac-keep">
            {jacSeen && (
              <div hidden={inboxMode !== 'mail'} className="inbox-pane">
                <InboxView
                  lang={lang}
                  theme={theme}
                  isSuper={isSuper}
                  switcher={inboxSwitch}
                  onOpenDevCenter={() => { setAdminFocus('dev', 'flows'); navigate('admin'); }}
                />
              </div>
            )}
            {inboxMode === 'chat' && (
              <ChatView lang={lang} me={user.email} switcher={inboxSwitch} />
            )}
            {inboxMode === 'customer' && <CustomerChatSoon lang={lang} switcher={inboxSwitch} />}
          </div>
        )}
        {activeSection === 'notifications' && (
          <NotificationsView lang={lang} isSuper={isSuper} showActes={country.code === 'FR'} onNavigate={navigate} onBack={() => navigate(notifReturn)} />
        )}
        {activeSection === 'documents' && docsHub && (
          <ResourcesHubView lang={lang} country={country} onPick={(tab) => navigate('documents', tab)} />
        )}
        {activeSection === 'documents' && !docsHub && (
          <DocumentsView
            onBack={() => goSection('documents')}
            country={country}
            dept={dept}
            docsTab={docsTab}
            read={read}
            onToggleRead={handleToggleRead}
            onOpenArticle={setOpenArticle}
            lang={lang}
            onToast={showToast}
          />
        )}
        </Suspense>
      </main>

      {/* ── LA PALETTE, AU-DESSUS DE TOUT ──
          It was mounted inside the Handover tab, below three early returns —
          so it appeared on the LIST and vanished the moment you opened a
          handover, which is precisely where it is most useful. It floats
          (position: fixed) and decides its own visibility from a store, so its
          mount point belongs here, above every section's returns: it draws
          nothing until a screen publishes something to search, and it follows
          you from the list into a handover and on into a case. */}
      <HandoverPalette lang={lang} />

      {/* Super admins only while it is being tried out. Kevo opens from the
          floating bar at the bottom; KevoBar is that bar on the pages that
          have none of their own. */}
      {isSuper && (
        <Suspense fallback={null}>
          <KevoAgent />
          <KevoBar lang={lang} country={country?.code || ''} hidden={activeSection === 'inbox'} />
        </Suspense>
      )}

      {openArticle && (
        <ArticleDrawer
          article={openArticle}
          completed={read}
          onToggleRead={handleToggleRead}
          onClose={() => setOpenArticle(null)}
        />
      )}

      {profileOpen && <Profile lang={lang} onClose={() => setProfileOpen(false)} country={country} dept={dept} isSuper={isSuper} />}
      {/* The planner's floating stopwatch/launcher. Role default: Recurring ON,
          super admins OFF — each person overrides it in the planner's Ajustes. */}
      {canPlanner && (
        <PlannerTimerChip
          inPlanner={activeSection === 'planner'}
          defaultOn={!isSuper}
          onOpen={() => navigate('planner')}
        />
      )}

      {feedbackOpen && (
        <Suspense fallback={null}>
          <FeedbackModal
            lang={lang}
            country={country.code}
            email={user.email}
            onClose={() => setFeedbackOpen(false)}
            onToast={showToast}
          />
        </Suspense>
      )}

      <Toast toast={toast} onDone={() => { window.clearTimeout(toastTimer.current); setToast(null); }} />
    </div>
  );
}

/** Shown to a signed-in @lexidy.com viewer whose account a super admin has
 *  denied, or to someone who has left the firm. */
function AccessPending({ lang, status, onSignOut }: { lang: Lang; status: 'denied' | 'closed'; onSignOut: () => void }) {
  const t = status === 'closed' ? {
    title: { en: 'Access closed', es: 'Acceso cerrado', fr: 'Accès fermé', pt: 'Acesso encerrado' },
    body: { en: 'This account no longer has access to the Agent Portal.', es: 'Esta cuenta ya no tiene acceso al Agent Portal.', fr: 'Ce compte n’a plus accès à l’Agent Portal.', pt: 'Esta conta já não tem acesso ao Agent Portal.' },
  } as const : {
    title: { en: 'Access not granted', es: 'Acceso no concedido', fr: 'Accès non accordé', pt: 'Acesso não concedido' },
    body: { en: 'An administrator has denied access to this account. Contact an administrator if you think this is a mistake.', es: 'Un administrador ha denegado el acceso a esta cuenta. Contacta a un administrador si crees que es un error.', fr: 'Un administrateur a refusé l’accès à ce compte. Contactez un administrateur.', pt: 'Um administrador negou o acesso a esta conta. Contate um administrador.' },
  } as const;
  return (
    <div className="onb">
      <div className="mywork" style={{ maxWidth: 520 }}>
        <span className="mywork-badge">—</span>
        <h1 className="mywork-h1">{t.title[lang]}</h1>
        <p className="mywork-lede">{t.body[lang]}</p>
        <button className="btn btn-ghost" onClick={onSignOut}>{lang === 'es' ? 'Cerrar sesión' : 'Sign out'}</button>
      </div>
    </div>
  );
}

/** Placeholder for a jurisdiction whose "My work" is not yet available
 *  (Portugal is currently suspended — Resources stays live for that team). */
function ComingSoon({ lang, country }: { lang: Lang; country: string }) {
  const t = {
    badge: { en: 'Coming soon', es: 'Muy pronto', fr: 'Bientôt', pt: 'Em breve' },
    title: { en: 'Not yet available', es: 'Aún no disponible', fr: 'Pas encore disponible', pt: 'Ainda não disponível' },
    body: {
      en: `“My work” for ${country} is being set up. In the meantime, the Resources section is fully available for your team.`,
      es: `“Mi trabajo” para ${country} se está preparando. Mientras tanto, la sección de Recursos está totalmente disponible para tu equipo.`,
      fr: `« Mon travail » pour ${country} est en cours de préparation. En attendant, la section Ressources est entièrement disponible pour votre équipe.`,
      pt: `“Meu trabalho” para ${country} está sendo preparado. Enquanto isso, a secção de Recursos está totalmente disponível para a sua equipa.`,
    },
  } as const;
  return (
    <div className="mywork">
      <span className="mywork-badge">{t.badge[lang]}</span>
      <h1 className="mywork-h1">{t.title[lang]}</h1>
      <p className="mywork-lede">{t.body[lang]}</p>
    </div>
  );
}

/** Shown while the jurisdiction redirect resolves — a beat of feedback instead
 *  of a blank document, which is what a white screen actually is. */
function BootWait() {
  return (
    <div className="crash">
      <div className="crash-card">
        <div className="mw-spin lg" />
        <p className="crash-sub">Loading your workspace…</p>
      </div>
    </div>
  );
}
